import { useEffect, useMemo, useState } from "react"
import { isTauri } from "@/lib/db/db"
import { getLocalSetting } from "@/lib/db/repos"
import { useAuth } from "@/hooks/useAuth"
import { useCondominium } from "@/hooks/useCondominium"
import { moduleIncluded, resolveOrgModules, type AppModule } from "@/lib/modules"

export type { AppModule } from "@/lib/modules"

const ALL_WEB_MODULES: AppModule[] = ["access_control", "time_clock", "full_itacondo"]
const DEFAULT_DESKTOP_MODULES: AppModule[] = ["access_control"]

const normalizeModules = (raw: string | null): AppModule[] => {
  if (!raw) return []

  const normalized = raw
    .split(/[,\s;|]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)

  const modules = normalized
    .map((item) => {
      if (item === "controle_acesso" || item === "controle-de-acesso" || item === "access") return "access_control"
      if (item === "ponto_digital" || item === "ponto-eletronico" || item === "timeclock") return "time_clock"
      if (item === "full" || item === "suite" || item === "itacondo") return "full_itacondo"
      return item
    })
    .filter((item): item is AppModule =>
      item === "access_control" || item === "time_clock" || item === "full_itacondo"
    )

  return Array.from(new Set(modules))
}

// Desktop: módulos vêm da licença ativada na máquina.
const useDesktopModules = (enabled: boolean) => {
  const [modules, setModules] = useState<AppModule[]>(DEFAULT_DESKTOP_MODULES)
  const [loading, setLoading] = useState(enabled)

  useEffect(() => {
    if (!enabled) return
    let mounted = true

    const loadModules = async () => {
      try {
        const configuredModules = normalizeModules(await getLocalSetting("enabled_modules"))
        const licenseModules = normalizeModules(await getLocalSetting("license_module"))
        const resolvedModules = configuredModules.length > 0 ? configuredModules : licenseModules

        if (mounted) {
          setModules(resolvedModules.length > 0 ? resolvedModules : DEFAULT_DESKTOP_MODULES)
        }
      } catch (error) {
        console.warn("[Modules] Nao foi possivel carregar modulos habilitados:", error)
        if (mounted) setModules(DEFAULT_DESKTOP_MODULES)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadModules()

    return () => {
      mounted = false
    }
  }, [enabled])

  return { modules, loading }
}

export const useEnabledModules = () => {
  const desktop = isTauri()
  const desktopModules = useDesktopModules(desktop)

  // Web: módulos vêm da organização do usuário logado (condominiums.enabled_modules).
  const { profile, loading: authLoading } = useAuth()
  const { data: condominium, isLoading: condominiumLoading, isError } = useCondominium()

  const webModules = useMemo<AppModule[]>(() => {
    // Sem organização (ex.: superadmin) ou falha de leitura: não restringe no client; o RLS segue valendo.
    if (!profile?.condominium_id || isError || !condominium) return ALL_WEB_MODULES
    return resolveOrgModules(condominium)
  }, [profile?.condominium_id, condominium, isError])

  const enabledModules = desktop ? desktopModules.modules : webModules
  const loading = desktop
    ? desktopModules.loading
    : Boolean(authLoading) || (!!profile?.condominium_id && condominiumLoading)

  const hasModule = useMemo(
    () => (module: AppModule) => moduleIncluded(enabledModules, module),
    [enabledModules]
  )

  return { enabledModules, hasModule, loading }
}
